import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import api from "../lib/api";
import { SIGNAGE_TYPES, VEHICLE_TYPES } from "../data/site";
import { Reveal } from "./Reveal";

const empty = {
  name: "", business_name: "", phone: "", email: "",
  signage_type: "", vehicle_type: "", num_vehicles: "", message: "",
};

const inputCls =
  "w-full bg-ink-900 border border-line text-white placeholder-white/35 focus:border-orange focus:outline-none focus:ring-1 focus:ring-orange px-4 py-3 transition-colors";

export const QuoteForm = () => {
  const [form, setForm] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files).slice(0, 5);
    const encoded = await Promise.all(
      files.map(
        (f) =>
          new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res({ name: f.name, data: r.result });
            r.readAsDataURL(f);
          })
      )
    );
    setPhotos((p) => [...p, ...encoded].slice(0, 5));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", { ...form, photos: photos.map((p) => p.data) });
      toast.success("Quote request sent! We'll be in touch shortly.");
      setForm(empty);
      setPhotos([]);
    } catch {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="relative border-y border-line bg-ink-800/40">
      <div className="mx-auto max-w-4xl px-5 md:px-8 py-24 md:py-32">
        <Reveal>
          <span className="overline">Free Quote</span>
          <h2 className="mt-3 font-head font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            Request Your Free Quote
          </h2>
          <p className="mt-4 text-white/60 max-w-2xl">
            Send us your details and any photos of your vehicles or premises — we'll get back to you fast.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form data-testid="quote-form" onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-4">
            <input data-testid="quote-name" className={inputCls} placeholder="Name *" value={form.name} onChange={set("name")} />
            <input data-testid="quote-business" className={inputCls} placeholder="Business Name" value={form.business_name} onChange={set("business_name")} />
            <input data-testid="quote-phone" className={inputCls} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
            <input data-testid="quote-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />

            <select data-testid="quote-signage-type" className={inputCls} value={form.signage_type} onChange={set("signage_type")}>
              <option value="">Type of Signage</option>
              {SIGNAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select data-testid="quote-vehicle-type" className={inputCls} value={form.vehicle_type} onChange={set("vehicle_type")}>
              <option value="">Vehicle Type</option>
              {VEHICLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>

            <input data-testid="quote-num" className={inputCls} placeholder="Number of Vehicles" value={form.num_vehicles} onChange={set("num_vehicles")} />

            <label className={`${inputCls} flex items-center gap-2 cursor-pointer text-white/50`}>
              <Upload size={18} className="text-orange" />
              <span>Upload Photos</span>
              <input data-testid="quote-photos" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
            </label>

            {photos.length > 0 && (
              <div className="sm:col-span-2 flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p.data} alt={p.name} className="h-16 w-16 object-cover border border-line" />
                    <button type="button" onClick={() => setPhotos(photos.filter((_, x) => x !== i))}
                      className="absolute -top-2 -right-2 bg-orange text-white rounded-full p-0.5">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <textarea data-testid="quote-message" rows={4} className={`${inputCls} sm:col-span-2 resize-none`} placeholder="Message" value={form.message} onChange={set("message")} />

            <button
              data-testid="quote-submit"
              type="submit"
              disabled={loading}
              className="sm:col-span-2 flex items-center justify-center gap-2 bg-orange hover:bg-orange-hover disabled:opacity-60 text-white font-head font-bold uppercase tracking-wide px-8 py-4 transition-colors"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : null}
              Request My Free Quote
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
